import { useEffect, useRef } from 'react';
import { message } from 'antd';
import QuoteStore from '../../../../stores/QuoteStore';
import UserStore from '../../../../stores/UserStore';
import { IPartFileRecord } from '@partsbadger/types';

interface Params {
    openModal: boolean;
    authModalVisible: boolean;
    setOpenModal: (v: boolean) => void;
}

export const useQuotePartsHandlers = ({ openModal, authModalVisible, setOpenModal }: Params) => {
    const cleanExpandedPart = () => {
        QuoteStore.setExpandedPartId(null);
    };

    /** When true, we're in the middle of submitParts() for multiple files; skip the single-part useEffect. */
    const batchSubmitInProgressRef = useRef(false);
    /** When true, the multi-part modal was shown this session; don't auto-upload single parts (avoids duplicates when user later clicks Continue). */
    const multiPartModalWasOpenedRef = useRef(false);
    /** One attempt to open a draft after a logged-in upload found no quote. */
    const ensureDraftStartedRef = useRef(false);

    useEffect(() => {
        const len = QuoteStore.fileParts.length;
        if (len === 0) {
            batchSubmitInProgressRef.current = false;
            multiPartModalWasOpenedRef.current = false;
        } else if (openModal && len > 1) {
            multiPartModalWasOpenedRef.current = true;
        }
    }, [QuoteStore.fileParts.length, openModal]);

    // Re-run when a quote appears: parts held back while there was none (e.g. a
    // guest who just finished sign-up, PTB-7157) are queued as soon as it exists.
    const quoteUid = QuoteStore.getQuoteUid;

    useEffect(() => {
        if (openModal || authModalVisible) return;
        if (QuoteStore.fileParts.length === 0) return;

        if (!quoteUid) {
            // Logged-in empty page (the quote link 404'd or the draft request failed).
            // Opening her draft here lets the effect enqueue as soon as the uid exists,
            // instead of waiting 10s and failing with "quote start still in progress".
            if (!UserStore.isAuthenticated || QuoteStore.quoteLoading) return;
            if (ensureDraftStartedRef.current) return;
            ensureDraftStartedRef.current = true;
            const email = UserStore.user?.default_contact?.email || UserStore.user?.email;
            if (!email) {
                message.error('Unable to load your last quote. Please contact support for assistance.');
                return;
            }
            void QuoteStore.getLastQuoteByEmail(email).catch(err => {
                ensureDraftStartedRef.current = false;
                message.error(`Unable to load your last quote. Please contact support for assistance. Error: ${err}.`);
            });
            return;
        }

        if (batchSubmitInProgressRef.current) return;
        if (multiPartModalWasOpenedRef.current) return;

        const timeout = setTimeout(() => {
            if (multiPartModalWasOpenedRef.current) return;
            if (QuoteStore.fileParts.length === 1) {
                cleanExpandedPart();
                QuoteStore.enqueuePartUploads(QuoteStore.fileParts);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [QuoteStore.fileParts, QuoteStore.quoteLoading, openModal, authModalVisible, quoteUid]);

    const submitParts = () => {
        if (!QuoteStore.fileParts.length) return;

        batchSubmitInProgressRef.current = true;
        cleanExpandedPart();
        setOpenModal(false);
        QuoteStore.enqueuePartUploads(QuoteStore.fileParts);
    };

    const removePart = (part: IPartFileRecord) => {
        QuoteStore.removePart(part);
        if (QuoteStore.fileParts.length < 1) {
            setOpenModal(false);
        }
    };

    return {
        submitParts,
        removePart,
        cleanExpandedPart,
    };
};
